/* global AbortController */
import { Controller } from "@hotwired/stimulus"
import SignupFormHelper from "../helpers/signup_form_helper"

// Mounted on self_service_overrides/landing/signups/new.html.haml. Shares SignupFormHelper with
// invitations_controller.js.
export default class extends Controller {
  static get values() { return { currentPlan: String }; }
  static get classes() { return ["errorBlock"]; }

  connect() {
    this.abortController = new AbortController();
    this.$root = $(this.element);
    this.$planChooser = this.$root.find(".plan-wrapper");
    this.$personalForm = this.$root.find(".personal-form");
    this.$businessForm = this.$root.find(".business-form");
    this.$subdomainPreview = this.$root.find(".business-form .subdomain-preview");
    this.$businessQuestionnaire = this.$root.find(".business-questionnaire");
    this.subdomainEditContainer = this.$root.find(".business-form .user-subdomain .subdomain-edit")[0];
    this.subdomainVisualizeContainer = this.$root.find(".business-form .user-subdomain .subdomain-visualize")[0];
    this.subdomainInput = this.$root.find(".business-form .user-subdomain .form-control")[0];
    this.companyInput = this.$businessForm.find(".user_company_name .form-control")[0];
    this.emailInput = this.$root.find(".user_email input.form-control")[0];
    this.firstNameInput = this.$root.find(".user_first_name input.form-control")[0];
    this.lastNameInput = this.$root.find(".user_last_name input.form-control")[0];
    this.subdomainSpan = this.$subdomainPreview.find("span");

    this.onPageLoad();
  }

  disconnect() {
    this.abortController.abort();
  }

  showForm(event) {
    this.doShowForm(event.params.plan, true);
  }

  showQuestionnaire() {
    SignupFormHelper.hideTermsModal("#business-terms");
    this.hideForms();
    this.$businessQuestionnaire.removeClass("hide");
    this.initQuestionnaire();
  }

  showSubdomainInput() {
    this.subdomainEditContainer.classList.remove("hide");
    this.subdomainVisualizeContainer.classList.add("hide");
  }

  hideSubdomainInput() {
    this.subdomainVisualizeContainer.classList.remove("hide");
    this.subdomainEditContainer.classList.add("hide");
  }

  goBack() {
    this.$businessQuestionnaire.addClass("hide");
    this.doShowForm("business", false);
  }

  showTermsModal(event) {
    SignupFormHelper.showTermsModal(event.params.selector);
  }

  togglePasswordConfirmationInput(event) {
    const form = event.currentTarget.closest(".business-form") ? "business" : "personal";
    SignupFormHelper.togglePasswordConfirmationInput(form);
  }

  // --- internal, no direct data-action binding below this line ---

  showChooser() { this.$planChooser.removeClass("hide"); }
  hideChooser() { this.$planChooser.addClass("hide"); }

  hideForms() {
    this.$personalForm.addClass("hide");
    this.$businessForm.addClass("hide");
  }

  companyNameToSubdomain(companyName) {
    const from = "ãàáäâẽèéëêìíïîõòóöôùúüûñç·/_,:;";
    const to = "aaaaaeeeeeiiiiooooouuuunc------";

    const newText = companyName.split("").map(
      (letter, i) => letter.replace(new RegExp(from.charAt(i), "g"), to.charAt(i))
    );

    return newText
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/&/g, "-y-")
      .replace(/[^\w\-]+/g, "")
      .replace(/\-\-+/g, "-");
  }

  giftingGoalValid(checkboxes, otherInput) {
    return checkboxes.map(c => c.checked).includes(true) || otherInput.value != "";
  }

  jobFunctionValid(radios, otherInput) {
    return radios.map(r => r.checked).includes(true) || otherInput.value != "";
  }

  initQuestionnaire() {
    const jobFunctionRadios = this.$businessQuestionnaire.find('.job-functions input[type="radio"]').toArray();
    const giftingGoalCheckboxes = this.$businessQuestionnaire.find('.gifting-goals input[type="checkbox"]').toArray();
    const otherJobFunction = this.$businessQuestionnaire.find(".job-functions .input-other input")[0];
    const otherGiftingGoal = this.$businessQuestionnaire.find(".gifting-goals .input-other input")[0];
    const submitBtn = this.$businessQuestionnaire.find(".btn-primary")[0];

    jobFunctionRadios.pop(); // remove other radio from array
    giftingGoalCheckboxes.pop(); // remove other check from array

    const updateSubmitGate = () => {
      if (this.jobFunctionValid(jobFunctionRadios, otherJobFunction) &&
        this.giftingGoalValid(giftingGoalCheckboxes, otherGiftingGoal)) {
        submitBtn.disabled = false;
      } else {
        submitBtn.disabled = true;
      }
    };

    const { signal } = this.abortController;

    jobFunctionRadios.concat(giftingGoalCheckboxes).forEach((input) => {
      input.addEventListener("change", updateSubmitGate, { signal });
    });

    [otherJobFunction, otherGiftingGoal].forEach((input) => {
      input.addEventListener("input", updateSubmitGate, { signal });
    });

    // Bound to the whole group, not just the "Other" input: both handlers read "Other"'s checked
    // state, which changes whenever any sibling in the group is picked.
    const allJobFunctionRadios = this.$businessQuestionnaire.find('.job-functions input[type="radio"]').toArray();
    const otherJobFunctionRadio = allJobFunctionRadios[allJobFunctionRadios.length - 1];
    allJobFunctionRadios.forEach((radio) => {
      radio.addEventListener("change", () => this.updateJobFunction(otherJobFunctionRadio), { signal });
    });

    const allGiftingGoalCheckboxes = this.$businessQuestionnaire.find('.gifting-goals input[type="checkbox"]').toArray();
    const otherGiftingGoalCheckbox = allGiftingGoalCheckboxes[allGiftingGoalCheckboxes.length - 1];
    allGiftingGoalCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", () => this.updateGiftingGoals(otherGiftingGoalCheckbox), { signal });
    });
  }

  updateJobFunction(otherRadio) {
    let jobFunctionsOther = this.$businessQuestionnaire.find(".job-functions .input-other")[0];
    let jobFunctionsOtherInput = jobFunctionsOther.children[0];
    let updateEvent = new Event("input");

    if (otherRadio.checked) {
      jobFunctionsOther.classList.remove("hide");
      jobFunctionsOtherInput.value = "";
      jobFunctionsOtherInput.dispatchEvent(updateEvent);
    } else {
      jobFunctionsOther.classList.add("hide");
    }
  }

  updateGiftingGoals(otherCheckbox) {
    let giftingGoalsOther = this.$businessQuestionnaire.find(".gifting-goals .input-other")[0];
    let giftingGoalsOtherInput = giftingGoalsOther.children[0];
    let updateEvent = new Event("input");

    if (otherCheckbox.checked) {
      giftingGoalsOther.classList.remove("hide");
    } else {
      giftingGoalsOther.classList.add("hide");
      giftingGoalsOtherInput.value = "";
      giftingGoalsOtherInput.dispatchEvent(updateEvent);
    }
  }

  setSubdomainSpan(useSubdomainInput, updateSubdomainInput) {
    let companySlug;

    if (useSubdomainInput) {
      companySlug = this.companyNameToSubdomain(this.subdomainInput.value);
    } else {
      companySlug = this.companyNameToSubdomain(this.companyInput.value);
    }

    // There are two places where to update the subdomain input value:
    this.subdomainSpan[0].textContent = companySlug + ".loopandtie.com";
    this.subdomainSpan[1].textContent = companySlug + ".loopandtie.com";

    if (updateSubdomainInput) {
      this.subdomainInput.value = companySlug;
    }
  }

  removeSubdomainErrorMessage(businessForm) {
    const $subdomainField = businessForm.find(".form-group.user-subdomain");
    const $subdomainErrorMsg = $subdomainField.find(".help-block");

    if ($subdomainField[0].classList.contains("has-error")) {
      $subdomainField[0].classList.remove("has-error");
      $subdomainField[0].removeChild($subdomainErrorMsg[0]);
    }
  }

  removeCompanyNameErrorMessage(businessForm) {
    const $companyNameField = businessForm.find(".form-group.user_company_name");
    const $companyNameErrorMsg = $companyNameField.find(`.${this.errorBlockClass}`);

    if ($companyNameField[0].classList.contains("has-error")) {
      $companyNameField[0].classList.remove("has-error");
      $companyNameField[0].removeChild($companyNameErrorMsg[0]);
    }
  }

  initBusinessForm() {
    const $form = this.$businessForm.find("form");

    SignupFormHelper.enableFormValidation($form);
    SignupFormHelper.initTermsModal("#business-terms", $form);

    this.setSubdomainSpan(true, false);

    const { signal } = this.abortController;

    this.companyInput.addEventListener("focusout", () => {
      if (this.companyInput.value === "") {
        this.companyInput.closest(".form-group").classList.add("has-error");

        if (this.$businessForm.find(`.${this.errorBlockClass}`).length === 0) {
          const errorMessage = document.createElement("div");
          errorMessage.classList.add(this.errorBlockClass);
          errorMessage.textContent = "invalid company name";
          this.companyInput.parentNode.after(errorMessage);
        }
      }
    }, { signal });

    this.companyInput.addEventListener("input", () => {
      this.removeCompanyNameErrorMessage($form);
      this.removeSubdomainErrorMessage($form);
      this.setSubdomainSpan(false, true);
    }, { signal });

    this.subdomainInput.addEventListener("input", () => {
      this.removeSubdomainErrorMessage($form);
      this.setSubdomainSpan(true, false);
    }, { signal });

    $form[0].addEventListener("submit", () => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "sign_up",
        account_type: "Business",
        user_data: {
          email: this.emailInput.value,
          first_name: this.firstNameInput.value,
          last_name: this.lastNameInput.value
        }
      });
    }, { signal });

    this.initQuestionnaire();
  }

  initPersonalForm() {
    const $form = this.$personalForm.find("form");
    $form[0].addEventListener("submit", () => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "sign_up",
        account_type: "Personal",
        user_data: {
          email: this.emailInput.value,
          first_name: this.firstNameInput.value,
          last_name: this.lastNameInput.value
        }
      });
    }, { signal: this.abortController.signal });

    SignupFormHelper.enableFormValidation($form);
    SignupFormHelper.initTermsModal("#personal-terms", $form);
  }

  doShowForm(plan, initialize) {
    this.updateStepUrlParam(plan);
    this.hideChooser();

    switch (plan) {
      case "personal":
        this.$personalForm.removeClass("hide");

        if (initialize) {
          this.initPersonalForm();
        }

        break;
      case "business":
        this.$businessForm.removeClass("hide");

        if (initialize) {
          this.initBusinessForm();
        }

        break;
    }
  }

  updateStepUrlParam(step) {
    let searchParams = new URLSearchParams(window.location.search);
    searchParams.set("s", step);

    let newRelativePathQuery = window.location.pathname + "?" + searchParams.toString();
    history.pushState(null, "", newRelativePathQuery);
  }

  onPageLoad() {
    let searchParams = new URLSearchParams(window.location.search);
    let plan = searchParams.get("s") || this.currentPlanValue;

    switch (plan) {
      case "personal":
        this.doShowForm("personal", true);
        break;
      case "business":
        this.doShowForm("business", true);
        break;
      default:
        this.hideForms();
        this.showChooser();
        break;
    }
  }
}
